import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faVideo,
  faEdit,
  faTimes,
  faCloudUploadAlt,
  faPlay,
  faHeart,
  faEye,
  faPlus,
  faTrash,
  faSearch,
  faFilter,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Button,
  Spinner,
  Badge,
  Table,
  Modal,
  Form,
} from "@themesberg/react-bootstrap";
import {
  DeleteApiRequest,
  PutApiRequest,
  PostApiRequest,
  GetApiRequest,
} from "../services/api";
import {
  fetchAllContent,
  setCurrentPage,
  setFilters,
  clearError,
} from "../redux/slices/contentSlice";
import { toast } from "../components/Toaster";

/** Maps API `status` (and legacy `isActive`) to admin list badge. */
function contentVisibilityBadge(c) {
  const status =
    c.status || (c.isActive === false ? "REMOVED_LEGACY" : "ACTIVE");
  if (status === "ACTIVE") return null;
  const labels = {
    PENDING: "Pending review",
    REMOVED_BY_OWNER: "Removed by author",
    REMOVED_BY_MODERATOR: "Removed by moderator",
    BANNED: "Banned",
    REMOVED_LEGACY: "Hidden (legacy)",
    HIDDEN_PENDING_REVIEW: "Auto-hidden (reports)",
  };
  const text = labels[status] || status;
  const variant =
    status === "BANNED" ? "danger" : status === "PENDING" ? "info" : "warning";
  return { text, variant };
}

const ContentPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    content: contentList,
    loading,
    error,
    pagination,
    currentPage,
    filters,
    limit,
    hasLoaded,
    lastFetchedPage,
    lastFetchedLimit,
    lastFetchedFilters,
  } = useSelector((state) => state.content);
  const [actionLoading, setActionLoading] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [experiencesPage, setExperiencesPage] = useState(1);
  const [experiencesTotalPages, setExperiencesTotalPages] = useState(1);
  const [experiencesLoadingMore, setExperiencesLoadingMore] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const experiencesListRef = useRef(null);
  const [footerStyle, setFooterStyle] = useState({});
  const [mediaType, setMediaType] = useState("image");
  const [formData, setFormData] = useState({
    description: "",
    fileUrls: [],
    fileUploadIds: [],
    videoUrl: "",
    videoUploadId: "",
    taggedExperiences: [],
  });

  useEffect(() => {
    const updateFooterBounds = () => {
      const main = document.querySelector("main.content");
      if (main) {
        const rect = main.getBoundingClientRect();
        setFooterStyle({
          position: "fixed",
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          bottom: 0,
          background: "#ffffff",
          borderTop: "1px solid #e5e7eb",
          padding: "0.75rem 1rem",
          zIndex: 1030,
        });
      }
    };
    updateFooterBounds();
    window.addEventListener("resize", updateFooterBounds);
    return () => window.removeEventListener("resize", updateFooterBounds);
  }, []);

  // Fetch content on mount if not already loaded (smart caching)
  useEffect(() => {
    if (!hasLoaded) {
      dispatch(fetchAllContent({ page: currentPage, limit, filters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shouldRefetch =
      hasLoaded &&
      (currentPage !== lastFetchedPage ||
        limit !== lastFetchedLimit ||
        JSON.stringify(filters) !== JSON.stringify(lastFetchedFilters));
    if (shouldRefetch) {
      dispatch(fetchAllContent({ page: currentPage, limit, filters }));
    }
  }, [
    currentPage,
    limit,
    filters,
    hasLoaded,
    dispatch,
    lastFetchedPage,
    lastFetchedLimit,
    lastFetchedFilters,
  ]);

  // Show error toast if loading fails
  useEffect(() => {
    if (error && hasLoaded) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch, hasLoaded]);

  const fetchExperiences = async (pageArg = 1) => {
    try {
      const response = await GetApiRequest("admin/experiences", {
        limit: 10,
        page: pageArg,
      });
      const data = response?.data?.data || {};
      const list = Array.isArray(data.experiences) ? data.experiences : [];
      const pages = data.pagination?.pages || 1;
      if (pageArg === 1) {
        setExperiences(list);
      } else {
        setExperiences((prev) => {
          const existingIds = new Set(prev.map((e) => e._id));
          const merged = [...prev];
          for (const item of list) {
            if (!existingIds.has(item._id)) merged.push(item);
          }
          return merged;
        });
      }
      setExperiencesPage(pageArg);
      setExperiencesTotalPages(pages);
    } catch (error) {
      console.error("Error fetching experiences:", error);
    } finally {
      setExperiencesLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchExperiences(1);
  }, []);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingFiles(true);
    try {
      if (mediaType !== "image") {
        setMediaType("image");
        setFormData((prev) => ({ ...prev, videoUrl: "", videoUploadId: "" }));
      }
      const formDataUpload = new FormData();
      for (let i = 0; i < files.length; i++) {
        formDataUpload.append("images", files[i]);
      }
      const response = await PostApiRequest("images/upload-multiple", formDataUpload);
      const images = response?.data?.data?.images || [];
      const uploadedIds = images.map((img) => img.uploadId).filter(Boolean);
      const previewUrls = images.map((img) => img.url).filter(Boolean);
      const urls =
        previewUrls.length === uploadedIds.length
          ? previewUrls
          : Array.from(files).map((f) => URL.createObjectURL(f));
      if (uploadedIds.length > 0) {
        setFormData((prev) => ({
          ...prev,
          fileUrls: [...prev.fileUrls, ...urls],
          fileUploadIds: [...prev.fileUploadIds, ...uploadedIds],
        }));
        toast.success(`${uploadedIds.length} image(s) uploaded!`);
      } else {
        toast.error("No images were uploaded");
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error(error?.response?.data?.message || "Failed to upload images");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploadingFiles(false);
    }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    try {
      if (mediaType !== "video") {
        setMediaType("video");
        setFormData((prev) => ({ ...prev, fileUrls: [], fileUploadIds: [] }));
      }
      const formDataUpload = new FormData();
      formDataUpload.append("video", file);
      const response = await PostApiRequest("videos/upload", formDataUpload);
      const data = response?.data?.data || {};
      const uploadId = data.uploadId ? String(data.uploadId).trim() : "";
      const rawUrl = data.url || response?.data?.url;
      const videoUrl =
        typeof rawUrl === "string" ? rawUrl.replace(/`/g, "").trim() : "";
      const previewUrl = videoUrl || URL.createObjectURL(file);
      if (uploadId) {
        setFormData((prev) => ({
          ...prev,
          videoUploadId: uploadId,
          videoUrl: previewUrl,
        }));
        toast.success(
          "Video uploaded successfully! Compression is processing in background.",
        );
      } else {
        throw new Error("No upload ID in response");
      }
    } catch (error) {
      console.error("Error uploading video:", error);
      toast.error(error?.response?.data?.message || "Failed to upload video");
    } finally {
      if (videoInputRef.current) videoInputRef.current.value = "";
      setUploadingVideo(false);
    }
  };

  const handleImageUpload = (e) => {
    uploadFiles(e.target.files);
  };
  const handleVideoUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      uploadVideo(e.target.files[0]);
    }
  };
  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      fileUrls: prev.fileUrls.filter((_, i) => i !== index),
      fileUploadIds: prev.fileUploadIds.filter((_, i) => i !== index),
    }));
  };
  const handleRemoveVideo = () => {
    setFormData((prev) => ({ ...prev, videoUrl: "", videoUploadId: "" }));
  };

  const toggleExperience = (expId) => {
    setFormData((prev) => ({
      ...prev,
      taggedExperiences: prev.taggedExperiences.includes(expId)
        ? prev.taggedExperiences.filter((id) => id !== expId)
        : [...prev.taggedExperiences, expId],
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.description.trim())
      errors.description = "Description is required";
    if (mediaType === "image") {
      const hasImages =
        formData.fileUploadIds.length > 0 ||
        (showEditModal && formData.fileUrls.length > 0);
      if (!hasImages) errors.media = "Please upload at least one image";
      if (formData.videoUploadId) errors.media = "Remove video; only images allowed";
    } else if (mediaType === "video") {
      const hasVideo =
        !!formData.videoUploadId ||
        (showEditModal && !!formData.videoUrl);
      if (!hasVideo) errors.media = "Please upload a video file";
      if (formData.fileUploadIds.length > 0)
        errors.media = "Remove images; only video allowed";
    }
    if (formData.taggedExperiences.length === 0)
      errors.experiences = "Tag at least one experience";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePageChange = (newPage) => {
    dispatch(setCurrentPage(newPage));
  };

  const handleCreateContent = async () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        description: formData.description.trim(),
        taggedExperiences: formData.taggedExperiences,
      };
      if (mediaType === "image" && formData.fileUploadIds?.length > 0) {
        payload.fileUploadIds = formData.fileUploadIds;
      }
      if (mediaType === "video" && formData.videoUploadId) {
        payload.videoUploadId = formData.videoUploadId.trim();
      }
      console.log("Creating content with payload:", payload);
      await PostApiRequest("content", payload);
      toast.success("Content created successfully!");
      setShowCreateModal(false);
      resetForm();
      dispatch(fetchAllContent({ page: currentPage, limit, filters }));
    } catch (error) {
      console.error("Error creating content:", error);
      toast.error(error?.response?.data?.message || "Failed to create content");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateContent = async () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        description: formData.description.trim(),
        taggedExperiences: formData.taggedExperiences,
      };
      if (mediaType === "image" && formData.fileUploadIds?.length > 0) {
        payload.fileUploadIds = formData.fileUploadIds;
      }
      if (mediaType === "video" && formData.videoUploadId) {
        payload.videoUploadId = formData.videoUploadId.trim();
      }
      console.log("Updating content with payload:", payload);
      await PutApiRequest(`content/${selectedContent._id}`, payload);
      toast.success("Content updated successfully!");
      setShowEditModal(false);
      resetForm();
      dispatch(fetchAllContent({ page: currentPage, limit, filters }));
    } catch (error) {
      console.error("Error updating content:", error);
      toast.error(error?.response?.data?.message || "Failed to update content");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteContent = async () => {
    setActionLoading(true);
    try {
      await DeleteApiRequest(`content/${selectedContent._id}`);
      toast.success("Content removed from public view");
      setShowDeleteModal(false);
      setSelectedContent(null);
      // Refetch after deletion
      dispatch(fetchAllContent({ page: currentPage, limit, filters }));
    } catch (error) {
      console.error("Error deleting content:", error);
      toast.error(error?.response?.data?.message || "Failed to remove content");
    } finally {
      setActionLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      description: "",
      fileUrls: [],
      fileUploadIds: [],
      videoUrl: "",
      videoUploadId: "",
      taggedExperiences: [],
    });
    setValidationErrors({});
  };

  const openEditModal = (content, e) => {
    e.stopPropagation();
    setSelectedContent(content);
    const hasImages =
      (Array.isArray(content.fileUploadIds) && content.fileUploadIds.length > 0) ||
      (Array.isArray(content.fileUrls) && content.fileUrls.length > 0);
    const hasVideo = !!(content.videoUploadId || content.videoUrl);
    const nextMediaType = hasImages ? "image" : hasVideo ? "video" : mediaType;
    setMediaType(nextMediaType);
    // Build image display URLs from fileAssets (urls.medium/high) or legacy fileUrls
    const imageUrls =
      content.fileAssets?.length > 0
        ? content.fileAssets.map(
            (a) => a.urls?.medium || a.urls?.high || a.urls?.low,
          ).filter(Boolean)
        : content.fileUrls || [];
    const imageIds = content.fileUploadIds || [];
    // Video display URL for preview: from videoAsset.urls or legacy videoUrl
    const videoDisplayUrl =
      content.videoAsset?.urls?.high ||
      content.videoAsset?.urls?.medium ||
      content.videoAsset?.urls?.original ||
      content.videoAsset?.thumbnail ||
      content.videoUrl ||
      "";
    setFormData({
      description: content.description || "",
      fileUrls: nextMediaType === "image" ? imageUrls : [],
      fileUploadIds: nextMediaType === "image" ? imageIds : [],
      videoUrl: nextMediaType === "video" ? videoDisplayUrl : "",
      videoUploadId: nextMediaType === "video" ? (content.videoUploadId || "") : "",
      taggedExperiences: content.taggedExperiences?.map((exp) => exp._id) || [],
    });
    setShowEditModal(true);
  };

  const handleRowClick = (contentId) => {
    history.push(`/content/${contentId}`);
  };

  return (
    <>
      <style>{`
        .page-header {
          background: #ffffff;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border-left: 4px solid #4f46e5;
        }
        
        .page-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }
        
        .page-header p {
          color: #64748b;
          font-size: 1rem;
          margin-bottom: 0;
        }
        
        .stats-badge {
          background: #f1f5f9;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #4f46e5;
          margin-top: 1rem;
        }
        
        .filter-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 1.5rem;
        }
        
        .filter-toggle-btn {
          background: #4f46e5;
          border: none;
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          transition: all 0.3s ease;
          color: white;
        }
        
        .filter-toggle-btn:hover {
          background: #4338ca;
          transform: translateY(-1px);
        }

        .create-btn {
          background: #4f46e5;
          border: none;
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          transition: all 0.3s ease;
          color: white;
        }

        .create-btn:hover {
          background: #4338ca;
          transform: translateY(-1px);
        }
        
        .search-input {
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          padding: 0.6rem 1rem;
          transition: all 0.3s ease;
        }
        
        .search-input:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        
        .filter-label {
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }
        
        .content-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .content-table table {
          margin-bottom: 0;
        }
        
        .content-table thead {
          background: #f8fafc;
        }
        
        .content-table th {
          font-weight: 700;
          color: #1e293b;
          padding: 1rem;
          border-bottom: 2px solid #e2e8f0;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .content-table tbody tr {
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .content-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .content-table td {
          padding: 1rem;
          vertical-align: middle;
          color: #475569;
        }
        
        .content-thumbnail {
          width: 80px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .video-thumbnail-wrapper {
          position: relative;
          width: 80px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
        }

        .video-play-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0, 0, 0, 0.6);
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        
        .content-description {
          font-size: 0.9rem;
          color: #475569;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .action-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          transition: all 0.3s ease;
          margin: 0 0.25rem;
        }
        
        .action-btn.edit {
          background: #4f46e5;
          color: white;
        }
        
        .action-btn.edit:hover {
          background: #4338ca;
          transform: scale(1.05);
        }
        
        .action-btn.delete {
          background: #ef4444;
          color: white;
        }
        
        .action-btn.delete:hover {
          background: #dc2626;
          transform: scale(1.05);
        }
        
        .pagination-container {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-top: 1.5rem;
        }
        
        .page-btn {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          transition: all 0.3s ease;
        }
        
        .page-btn:hover:not(:disabled) {
          background: #4f46e5;
          color: white;
          border-color: #4f46e5;
        }
        
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .empty-state svg {
          font-size: 3rem;
          color: #cbd5e0;
          margin-bottom: 1rem;
        }
        
        .empty-state h3 {
          color: #475569;
          margin-bottom: 0.5rem;
        }
        
        .empty-state p {
          color: #94a3b8;
        }
        
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 4rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .modal-content {
          border-radius: 12px;
          border: none;
        }
        
        .modal-header {
          background: #4f46e5;
          color: white;
          border-radius: 12px 12px 0 0;
          padding: 1.25rem 1.5rem;
        }

        .modal-title {
          color: white;
        }
        
        .modal-title {
          font-weight: 700;
        }
        
        .modal-header .btn-close {
          filter: brightness(0) invert(1);
        }
        
        .image-upload-zone {
          border: 2px dashed #cbd5e0;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #f8fafc;
        }
        
        .image-upload-zone:hover {
          border-color: #4f46e5;
          background: #f1f5f9;
        }
        
        .image-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .image-preview-item {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 1;
        }
        
        .image-preview-item img, .image-preview-item video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .image-remove-btn {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(239, 68, 68, 0.95);
          color: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .image-remove-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .experience-select-item {
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 2px solid #e2e8f0;
        }

        .experience-select-item:hover {
          border-color: #4f46e5;
          background: #f8fafc;
        }

        .experience-select-item.selected {
          background: #eff6ff;
          border-color: #4f46e5;
        }
        
        @media (max-width: 768px) {
          .content-table {
            overflow-x: auto;
          }
        }
      `}</style>

      <div className="page-header">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h2>Content Gallery</h2>
            <p>Manage and curate experience content</p>
            <div className="stats-badge">
              <FontAwesomeIcon icon={faImage} />
              {pagination?.total || 0} Total Items
            </div>
          </div>
          <Button
            className="create-btn"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create Content
          </Button>
        </div>
      </div>

      <div className="filter-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ fontWeight: 700, color: "#1e293b" }}>
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Filters
          </h5>
          <Button
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            {showFilters ? "Hide" : "Show"} Filters
          </Button>
        </div>

        {showFilters && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <div className="filter-label">Experience</div>
                <Form.Select
                  value={filters.experienceId}
                  onChange={(e) => {
                    dispatch(
                      setFilters({ ...filters, experienceId: e.target.value }),
                    );
                    dispatch(setCurrentPage(1));
                  }}
                  className="search-input"
                >
                  <option value="">All Experiences</option>
                  {experiences.map((exp) => (
                    <option key={exp._id} value={exp._id}>
                      {exp.title}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <div className="filter-label">Type</div>
                <Form.Select
                  value={filters.type}
                  onChange={(e) => {
                    dispatch(setFilters({ ...filters, type: e.target.value }));
                    dispatch(setCurrentPage(1));
                  }}
                  className="search-input"
                >
                  <option value="">All Types</option>
                  <option value="image">Images Only</option>
                  <option value="video">Videos Only</option>
                </Form.Select>
              </Col>
            </Row>

            <Button
              variant="outline-secondary"
              onClick={() => {
                dispatch(setFilters({ experienceId: "", type: "" }));
                dispatch(setCurrentPage(1));
              }}
              style={{ borderRadius: "8px", fontWeight: 600 }}
            >
              Clear Filters
            </Button>
          </>
        )}
      </div>

      {/* Initial load - show nothing while fetching */}
      {loading && !hasLoaded ? (
        <div className="loading-container" style={{ minHeight: "300px" }}>
          {/* Silent loading on first mount */}
        </div>
      ) : loading && hasLoaded ? (
        <div className="loading-container">
          <Spinner
            animation="border"
            style={{ color: "#4f46e5", width: "3rem", height: "3rem" }}
          />
        </div>
      ) : contentList.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faSearch} />
          <h3>No Content Found</h3>
          <p>Try adjusting your filters or create new content</p>
        </div>
      ) : (
        <>
          <div className="content-table">
            <Table responsive hover>
              <thead>
                <tr>
                  <th style={{ width: "100px" }}>Media</th>
                  <th>Description</th>
                  <th style={{ width: "120px" }}>Type</th>
                  <th style={{ width: "200px" }}>Tagged Experiences</th>
                  <th style={{ width: "100px" }}>Stats</th>
                  <th style={{ width: "120px" }} className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {contentList.map((content) => {
                  const hasImages =
                    (content.fileAssets?.length > 0) ||
                    (content.fileUrls?.length > 0);
                  const imageCount =
                    content.fileAssets?.length ?? content.fileUrls?.length ?? 0;
                  const imageThumbUrl =
                    content.fileAssets?.[0]?.urls?.medium ||
                    content.fileUrls?.[0];
                  const hasVideo =
                    !!content.videoAsset || !!content.videoUrl;
                  const videoThumbUrl =
                    content.videoAsset?.thumbnail ||
                    content.videoAsset?.urls?.thumbnail ||
                    content.videoUrl;
                  const visBadge = contentVisibilityBadge(content);
                  return (
                  <tr
                    key={content._id}
                    onClick={() => handleRowClick(content._id)}
                  >
                    <td>
                      {hasImages && imageThumbUrl ? (
                        <img
                          src={imageThumbUrl}
                          alt="content"
                          className="content-thumbnail"
                        />
                      ) : hasVideo && videoThumbUrl ? (
                        <div className="video-thumbnail-wrapper">
                          <img
                            src={videoThumbUrl}
                            alt="video"
                            className="content-thumbnail"
                          />
                          <div className="video-play-icon">
                            <FontAwesomeIcon
                              icon={faPlay}
                              size="sm"
                              style={{ marginLeft: "2px" }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "80px",
                            height: "60px",
                            background: "#f3f4f6",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faImage}
                            style={{ color: "#d1d5db" }}
                          />
                        </div>
                      )}
                    </td>
                    <td>
                      <div
                        className="content-description"
                        title={content.description}
                      >
                        {content.description || "No description"}
                      </div>
                      {visBadge && (
                        <Badge
                          bg={visBadge.variant}
                          text={visBadge.variant === "warning" ? "dark" : undefined}
                          className="mt-1"
                          style={{ fontSize: "0.7rem" }}
                        >
                          {visBadge.text}
                        </Badge>
                      )}
                    </td>
                    <td>
                      <Badge
                        bg={hasImages ? "primary" : "warning"}
                        style={{
                          fontSize: "0.8rem",
                          padding: "0.4rem 0.75rem",
                        }}
                      >
                        {hasImages
                          ? `${imageCount} Image${imageCount > 1 ? "s" : ""}`
                          : "Video"}
                      </Badge>
                    </td>
                    <td>
                      {content.taggedExperiences?.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.25rem",
                          }}
                        >
                          {content.taggedExperiences.slice(0, 2).map((exp) => (
                            <Badge
                              key={exp._id}
                              bg="secondary"
                              style={{
                                fontSize: "0.75rem",
                                padding: "0.3rem 0.6rem",
                              }}
                            >
                              {exp.title}
                            </Badge>
                          ))}
                          {content.taggedExperiences.length > 2 && (
                            <span
                              style={{
                                fontSize: "0.75rem",
                                color: "#9ca3af",
                                padding: "0.3rem",
                              }}
                            >
                              +{content.taggedExperiences.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                          None
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: "0.85rem" }}>
                        <div style={{ marginBottom: "0.25rem" }}>
                          <FontAwesomeIcon
                            icon={faHeart}
                            className="text-danger me-1"
                          />
                          {content.likeCount || 0}
                        </div>
                        <div>
                          <FontAwesomeIcon
                            icon={faEye}
                            className="text-info me-1"
                          />
                          {content.viewCount || 0}
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <button
                        className="action-btn edit"
                        onClick={(e) => openEditModal(content, e)}
                        disabled={actionLoading}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedContent(content);
                          setShowDeleteModal(true);
                        }}
                        disabled={actionLoading}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {contentList.length > 0 && (
            <div style={footerStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontWeight: 600, color: "#475569" }}>
                  Page {currentPage} of {pagination.pages || 1} (
                  {pagination.total || 0} total items)
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    className="page-btn"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(
                      1,
                      currentPage - Math.floor(maxVisible / 2),
                    );
                    let end = Math.min(
                      pagination.pages || 1,
                      start + maxVisible - 1,
                    );
                    if (end - start < maxVisible - 1) {
                      start = Math.max(1, end - maxVisible + 1);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button
                          key={i}
                          className="page-btn"
                          style={
                            i === currentPage
                              ? {
                                  background: "#4f46e5",
                                  color: "white",
                                  borderColor: "#4f46e5",
                                }
                              : {}
                          }
                          onClick={() => handlePageChange(i)}
                        >
                          {i}
                        </Button>,
                      );
                    }
                    return pages;
                  })()}
                  <Button
                    className="page-btn"
                    disabled={currentPage >= (pagination.pages || 1)}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
          {contentList.length > 0 && <div style={{ height: "64px" }} />}
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        show={showCreateModal || showEditModal}
        onHide={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {showEditModal ? "Edit Content" : "Create Content"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem" }}>
          {Object.keys(validationErrors).length > 0 && (
            <div
              style={{
                background: "#fef2f2",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1.5rem",
                border: "1px solid #fecaca",
              }}
            >
              <strong style={{ color: "#991b1b" }}>
                Please fix the following errors:
              </strong>
              <ul
                style={{
                  marginTop: "0.5rem",
                  marginBottom: 0,
                  paddingLeft: "1.5rem",
                }}
              >
                {Object.values(validationErrors).map((e, i) => (
                  <li key={i} style={{ color: "#7f1d1d" }}>
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginBottom: "2rem" }}>
            <h5
              style={{
                marginBottom: "1rem",
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              Basic Information
            </h5>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>
                Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe your content..."
                isInvalid={validationErrors.description}
                className="search-input"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.description}
              </Form.Control.Feedback>
            </Form.Group>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <h5
              style={{
                marginBottom: "1rem",
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              <FontAwesomeIcon icon={faImage} className="me-2" />
              Media
            </h5>
            <div
              style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}
            >
              <Button
                variant={
                  mediaType === "image" ? "primary" : "outline-secondary"
                }
                onClick={() => {
                  setMediaType("image");
                  setFormData((prev) => ({
                    ...prev,
                    videoUrl: "",
                    videoUploadId: "",
                  }));
                }}
              >
                Images
              </Button>
              <Button
                variant={
                  mediaType === "video" ? "primary" : "outline-secondary"
                }
                onClick={() => {
                  setMediaType("video");
                  setFormData((prev) => ({
                    ...prev,
                    fileUrls: [],
                    fileUploadIds: [],
                  }));
                }}
              >
                Video
              </Button>
            </div>

            {mediaType === "image" && (
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600 }}>Images</Form.Label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="d-none"
                />
                <div
                  className="image-upload-zone"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesomeIcon
                    icon={faCloudUploadAlt}
                    style={{
                      fontSize: "2.5rem",
                      color: "#cbd5e0",
                      marginBottom: "0.75rem",
                    }}
                  />
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#475569",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Click to upload images
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                    PNG, JPG up to 5MB each
                  </div>
                </div>
                {uploadingFiles && (
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "1rem",
                      color: "#4f46e5",
                    }}
                  >
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading images...
                  </div>
                )}
                {formData.fileUrls.length > 0 && (
                  <div className="image-preview-grid">
                    {formData.fileUrls.map((url, idx) => (
                      <div key={idx} className="image-preview-item">
                        <img src={url} alt={`Preview ${idx + 1}`} />
                        <button
                          className="image-remove-btn"
                          onClick={() => handleRemoveFile(idx)}
                        >
                          <FontAwesomeIcon icon={faTimes} size="xs" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>
            )}

            {mediaType === "video" && (
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600 }}>Video</Form.Label>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,video/x-flv,video/x-ms-wmv"
                  ref={videoInputRef}
                  onChange={handleVideoUpload}
                  className="d-none"
                />
                <div
                  className="image-upload-zone"
                  onClick={() => videoInputRef.current?.click()}
                >
                  <FontAwesomeIcon
                    icon={faVideo}
                    style={{
                      fontSize: "2.5rem",
                      color: "#cbd5e0",
                      marginBottom: "0.75rem",
                    }}
                  />
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#475569",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Click to upload video
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                    MP4, MOV, AVI, MKV, WebM, FLV, WMV up to 500MB
                  </div>
                </div>
                {uploadingVideo && (
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "1rem",
                      color: "#4f46e5",
                    }}
                  >
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading video...
                  </div>
                )}
                {formData.videoUrl && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      className="image-preview-item"
                      style={{ width: "200px", height: "150px" }}
                    >
                      <video src={formData.videoUrl} controls />
                      <button
                        className="image-remove-btn"
                        onClick={handleRemoveVideo}
                      >
                        <FontAwesomeIcon icon={faTimes} size="xs" />
                      </button>
                    </div>
                  </div>
                )}
              </Form.Group>
            )}
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <h5
              style={{
                marginBottom: "1rem",
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              Tag Experiences <span className="text-danger">*</span>
            </h5>
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "1rem",
                maxHeight: "300px",
                overflowY: "auto",
                background: "#f8fafc",
              }}
              ref={experiencesListRef}
              onScroll={(e) => {
                const el = e.currentTarget;
                const nearBottom =
                  el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
                if (
                  nearBottom &&
                  !experiencesLoadingMore &&
                  experiencesPage < experiencesTotalPages
                ) {
                  setExperiencesLoadingMore(true);
                  fetchExperiences(experiencesPage + 1);
                }
              }}
            >
              {experiences.map((exp) => (
                <div
                  key={exp._id}
                  onClick={() => toggleExperience(exp._id)}
                  className={`experience-select-item ${formData.taggedExperiences.includes(exp._id) ? "selected" : ""}`}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: "#1e293b" }}>
                        {exp.title}
                      </div>
                      {exp.description && (
                        <div
                          style={{
                            fontSize: "0.85rem",
                            color: "#6b7280",
                            marginTop: "0.25rem",
                          }}
                        >
                          {exp.description.substring(0, 50)}...
                        </div>
                      )}
                    </div>
                    {formData.taggedExperiences.includes(exp._id) && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{ color: "#4f46e5", fontSize: "1.25rem" }}
                      />
                    )}
                  </div>
                </div>
              ))}
              {experiencesLoadingMore && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "0.5rem",
                    color: "#4f46e5",
                  }}
                >
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading more...
                </div>
              )}
            </div>
            {validationErrors.experiences && (
              <div
                style={{
                  color: "#dc2626",
                  fontSize: "0.875rem",
                  marginTop: "0.5rem",
                }}
              >
                {validationErrors.experiences}
              </div>
            )}
            <small
              style={{
                color: "#6b7280",
                display: "block",
                marginTop: "0.5rem",
              }}
            >
              Click to select/deselect experiences
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ border: "none", padding: "1.25rem" }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              resetForm();
            }}
            disabled={actionLoading || uploadingFiles || uploadingVideo}
            style={{
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: "#4f46e5",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
            onClick={showEditModal ? handleUpdateContent : handleCreateContent}
            disabled={actionLoading || uploadingFiles || uploadingVideo}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {showEditModal ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={showEditModal ? faEdit : faPlus}
                  className="me-2"
                />
                {showEditModal ? "Save Changes" : "Create Content"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove content</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem" }}>
          {selectedContent && (
            <>
              <div
                style={{
                  background: "#fffbeb",
                  padding: "1.25rem",
                  borderRadius: "8px",
                  marginBottom: "1.25rem",
                  border: "1px solid #fde68a",
                }}
              >
                <h6 style={{ color: "#92400e", marginBottom: "0.75rem" }}>
                  Hide this post from the app
                </h6>
                <p
                  style={{
                    color: "#78350f",
                    marginBottom: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  The post will no longer appear in public feeds. It stays in the database for audit and recovery.
                </p>
              </div>

              <div
                style={{
                  padding: "1rem",
                  background: "#f8fafc",
                  borderRadius: "8px",
                }}
              >
                <p style={{ marginBottom: 0 }}>
                  <strong>Description:</strong>{" "}
                  {selectedContent.description?.substring(0, 100)}...
                </p>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ border: "none", padding: "1.25rem" }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedContent(null);
            }}
            disabled={actionLoading}
            style={{
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: "#ef4444",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
            onClick={handleDeleteContent}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Remove from public view"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ContentPage;
